// ---------- les deux façons de colorier ----------
// Le dialogue de la première feuille. Il remplace la bulle « poser » de coach.ts pour un joueur
// qui n'a encore rien peint, et la rangée « Revoir les gestes » des réglages le rouvre.
//
// Pourquoi un dialogue là où des bulles suffisaient à ne rien demander : elles apprenaient
// le tap, et le balayage n'arrivait qu'au CONSTAT de trois zones tapées une par une. Un joueur qui
// ne lisait pas cette deuxième bulle — elle arrive pendant qu'il joue, c'est-à-dire pendant qu'il
// regarde ses cases — faisait toute sa partie au tap, et sur une grille de 120 cases de côté le
// jeu devient une corvée — constaté chez les nouveaux joueurs en septembre 2026. Les deux gestes sont
// donc montrés ENSEMBLE, avant la première case, et c'est la comparaison qui fait comprendre :
// un tap, une zone ; un trait, toutes celles qu'il traverse.
//
// Trois formes ont été maquettées (une carte, deux écrans, un essai au doigt dans le dialogue) ;
// la carte a été retenue : elle se lit d'un coup d'œil et tient sur un écran.
//
// Deux mini-feuilles en DOM et non en canevas : quarante cases chacune, et rien ici ne touche
// render.ts — le dialogue s'ouvre par-dessus la vraie feuille sans invalider aucune de ses
// couches. Les symboles sont ceux de l'alphabet Motifs, qui n'ont rien à traduire.
//
// Ce module ne connaît ni coach.ts ni ui.ts : il ouvre, anime, ferme, et rappelle celui qui l'a
// ouvert. C'est coach.ts qui sait ce que la fermeture veut dire.
//
// Depuis le 2026-10-31, c'est le renard du logo qui parle (src/mascot.ts), et la toute première
// ouverture commence par une page où il accueille et dit qu'un dessin est caché. Une page et non
// une ligne de plus sur la carte : la carte des gestes est déjà pleine, et une phrase glissée sous
// son titre se lisait comme une légende qu'on saute.

import { noMotion } from './anim';
import { say, talk } from './mascot';

type Glyph = 'P' | 'T' | 'H' | 'V';

/** Les tracés, dans une case de 10 × 10 : plein, triangle, demi-case haute, vide. */
const GLYPHS: Record<Glyph, string> = {
  P: '<rect width="10" height="10"/>',
  T: '<path d="M0 0V10H10Z"/>',
  H: '<rect width="10" height="5"/>',
  V: '',
};

/** Le symbole verrouillé des deux démonstrations. Le plein, parce qu'une zone pleine qui tombe
 *  se voit à 22 px ; une demi-case ou un triangle rempli se distingue mal de son repère. */
const TARGET: Glyph = 'P';

/** La feuille du tap : deux zones de taille différente (six cases, puis quatre), pour que le
 *  second tap montre que « toute la zone » ne veut pas dire « toutes les cases du symbole ». */
const TAP_GRID = ['VVHHVVVV', 'VPPHVTPV', 'VPPPVTPP', 'TVPVVVVP', 'TTVHHVVV'];
const TAPS: ReadonlyArray<[number, number]> = [
  [2, 2],
  [2, 6],
];

/** La feuille du balayage : quatre zones que le trait traverse, et entre elles des cases vides
 *  ou d'un autre symbole qu'il franchit sans rien peindre — c'est ce qui montre, sans une phrase,
 *  qu'un balayage ne peut pas être une faute. */
const SWIPE_GRID = ['VPVHVPPV', 'PPVTPVPV', 'VTVPPVTV', 'HVVPVVPP', 'VVTVVHVV'];
const ROUTE: ReadonlyArray<[number, number]> = [
  [1, 0],
  [1, 1],
  [1, 2],
  [1, 3],
  [1, 4],
  [1, 5],
  [1, 6],
  [2, 6],
  [3, 6],
];

/** Le côté d'une case, en px. Deux mini-feuilles de cinq rangées, leurs palettes et leurs
 *  phrases doivent tenir avec le bouton sur un écran de 640 px moins la bande publicitaire ;
 *  à 26, le bouton passait sous le pli d'un petit téléphone. */
const CELL = 22;
/** Le retrait de la feuille autour des cases (son padding, cf. .gd-sheet). */
const PAD = 4;

/** Une animation en cours. Tout ce qu'elle arme passe par elle, pour que la fermeture du
 *  dialogue arrête d'un coup les deux boucles — un minuteur oublié continuerait de remplir une
 *  mini-feuille invisible jusqu'au prochain rechargement. */
interface Run {
  alive: boolean;
  timers: number[];
}

const STOP = Symbol('stop');

function wait(run: Run, ms: number): Promise<void> {
  return new Promise((resolve, reject) => {
    run.timers.push(window.setTimeout(() => (run.alive ? resolve() : reject(STOP)), ms));
  });
}

function glyphSvg(g: Glyph): string {
  return `<svg viewBox="0 0 10 10" aria-hidden="true">${GLYPHS[g]}</svg>`;
}

class MiniSheet {
  readonly el: HTMLElement;
  private readonly cells: HTMLElement[][];
  private readonly legend: HTMLElement;
  private readonly finger: HTMLElement;
  private readonly trailPath: SVGPathElement;
  private filled: boolean[][] = [];
  fx = 0;
  fy = 0;

  constructor(
    host: HTMLElement,
    private readonly rows: readonly string[],
  ) {
    host.replaceChildren();
    this.el = document.createElement('div');
    this.el.className = 'gd-sheet';
    this.el.style.setProperty('--cols', String(rows[0]!.length));
    this.el.style.setProperty('--cs', `${CELL}px`);
    this.cells = rows.map((row) =>
      [...row].map((g) => {
        const d = document.createElement('i');
        d.innerHTML = glyphSvg(g as Glyph);
        this.el.appendChild(d);
        return d;
      }),
    );
    const trail = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    trail.classList.add('gd-trail');
    this.trailPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    trail.appendChild(this.trailPath);
    this.finger = document.createElement('span');
    this.finger.className = 'gd-finger';
    this.el.append(trail, this.finger);
    this.legend = document.createElement('div');
    this.legend.className = 'gd-legend';
    host.append(this.el, this.legend);
    this.reset();
  }

  reset(): void {
    this.filled = this.rows.map((row) => [...row].map(() => false));
    this.rows.forEach((row, r) =>
      [...row].forEach((g, c) => {
        const d = this.cells[r]![c]!;
        d.className = g === TARGET ? 'on' : '';
        d.style.removeProperty('--d');
      }),
    );
    this.trailPath.setAttribute('d', '');
    this.fingerState(false, false);
    this.drawLegend();
  }

  /** La légende de la vraie feuille, en miniature : le symbole verrouillé entouré, et le compte
   *  de ses cases qui descend à chaque zone. C'est le seul endroit où le joueur voit, avant de
   *  jouer, que la pastille qu'il choisira en bas décide des cases qui se remplissent. */
  private drawLegend(): void {
    this.legend.innerHTML = (['T', 'H', 'P'] as const)
      .map((g) => {
        const n = this.left(g);
        const cls = `gd-pill${g === TARGET ? ' on' : ''}${n === 0 ? ' done' : ''}`;
        return `<span class="${cls}">${glyphSvg(g)}${n}</span>`;
      })
      .join('');
  }

  private left(g: Glyph): number {
    let n = 0;
    this.rows.forEach((row, r) =>
      [...row].forEach((x, c) => {
        if (x === g && !this.filled[r]![c]) n++;
      }),
    );
    return n;
  }

  private paintable(r: number, c: number): boolean {
    return this.rows[r]?.[c] === TARGET && this.filled[r]?.[c] === false;
  }

  /** La règle de floodFrom() : voisinage à quatre, les cases déjà remplies font mur. Les cases
   *  éclosent par distance au doigt, comme sur la vraie feuille (animPop). */
  fill(r: number, c: number): boolean {
    if (!this.paintable(r, c)) return false;
    const queue: Array<[number, number, number]> = [[r, c, 0]];
    this.filled[r]![c] = true;
    while (queue.length) {
      const [y, x, dist] = queue.shift()!;
      const d = this.cells[y]![x]!;
      d.style.setProperty('--d', `${noMotion() ? 0 : dist * 55}ms`);
      d.className = 'done';
      for (const [dy, dx] of [
        [1, 0],
        [-1, 0],
        [0, 1],
        [0, -1],
      ] as const) {
        if (!this.paintable(y + dy, x + dx)) continue;
        this.filled[y + dy]![x + dx] = true;
        queue.push([y + dy, x + dx, dist + 1]);
      }
    }
    this.drawLegend();
    return true;
  }

  center(r: number, c: number): [number, number] {
    return [PAD + (c + 0.5) * CELL, PAD + (r + 0.5) * CELL];
  }

  cellAt(x: number, y: number): [number, number] | null {
    const r = Math.floor((y - PAD) / CELL);
    const c = Math.floor((x - PAD) / CELL);
    return r >= 0 && r < this.rows.length && c >= 0 && c < this.rows[0]!.length ? [r, c] : null;
  }

  put(x: number, y: number): void {
    this.fx = x;
    this.fy = y;
    this.finger.style.transform = `translate(${x}px, ${y}px)`;
  }

  fingerState(shown: boolean, down: boolean): void {
    this.finger.classList.toggle('on', shown);
    this.finger.classList.toggle('down', down);
  }

  ripple(x: number, y: number): void {
    const d = document.createElement('span');
    d.className = 'gd-ripple';
    d.style.left = `${x}px`;
    d.style.top = `${y}px`;
    this.el.appendChild(d);
    d.addEventListener('animationend', () => d.remove());
  }

  trail(d: string): void {
    this.trailPath.setAttribute('d', d);
  }
}

/** Le doigt glisse jusqu'à (x, y). `linear` pour le trait, qui doit peindre à vitesse
 *  constante ; ralenti à l'arrivée sinon, comme une main qui vise. */
function glide(
  run: Run,
  s: MiniSheet,
  x: number,
  y: number,
  ms: number,
  onStep?: () => void,
): Promise<void> {
  const x0 = s.fx;
  const y0 = s.fy;
  const t0 = performance.now();
  return new Promise((resolve, reject) => {
    const step = (now: number): void => {
      if (!run.alive) return reject(STOP);
      const k = Math.min(1, (now - t0) / ms);
      const e = onStep ? k : 1 - (1 - k) ** 3;
      s.put(x0 + (x - x0) * e, y0 + (y - y0) * e);
      onStep?.();
      if (k < 1) requestAnimationFrame(step);
      else resolve();
    };
    requestAnimationFrame(step);
  });
}

async function tapLoop(run: Run, s: MiniSheet): Promise<void> {
  for (;;) {
    s.reset();
    const [x0, y0] = s.center(TAPS[0]![0], TAPS[0]![1]);
    s.put(x0 + 30, y0 + 40);
    await wait(run, 500);
    s.fingerState(true, false);
    for (const [r, c] of TAPS) {
      const [x, y] = s.center(r, c);
      await glide(run, s, x, y, 650);
      await wait(run, 250);
      s.fingerState(true, true);
      s.ripple(x, y);
      s.fill(r, c);
      await wait(run, 180);
      s.fingerState(true, false);
      await wait(run, 900);
    }
    s.fingerState(false, false);
    await wait(run, 1600);
  }
}

async function swipeLoop(run: Run, s: MiniSheet): Promise<void> {
  const pts = ROUTE.map(([r, c]) => s.center(r, c));
  const [sx, sy] = pts[0]!;
  for (;;) {
    s.reset();
    s.put(sx - 20, sy + 34);
    await wait(run, 500);
    s.fingerState(true, false);
    await glide(run, s, sx, sy, 600);
    // Posé puis parti SANS PAUSE : 200 ms, sous les 350 de HOLD_TO_PAN_MS. Un doigt qui
    // s'attarderait ici montrerait le geste qui, sur la vraie feuille, déplace au lieu de peindre.
    await wait(run, 200);
    s.fingerState(true, true);
    s.ripple(sx, sy);
    let d = `M${sx - PAD} ${sy - PAD}`;
    const touch = (): void => {
      const rc = s.cellAt(s.fx, s.fy);
      if (rc) s.fill(rc[0], rc[1]);
    };
    touch();
    for (const [x, y] of pts.slice(1)) {
      await glide(run, s, x, y, 210, () => {
        touch();
        d += ` L${s.fx - PAD} ${s.fy - PAD}`;
        s.trail(d);
      });
    }
    await wait(run, 150);
    s.fingerState(true, false);
    await wait(run, 400);
    s.fingerState(false, false);
    await wait(run, 1800);
  }
}

/** Animations coupées : chaque feuille montre son geste arrêté, une fois fait. Le doigt reste
 *  posé et le trait tracé, sinon l'image ne dirait plus lequel des deux gestes a rempli quoi. */
function stillFrames(tap: MiniSheet, swipe: MiniSheet): void {
  const [tr, tc] = TAPS[0]!;
  tap.fill(tr, tc);
  tap.put(...tap.center(tr, tc));
  tap.fingerState(true, false);
  const pts = ROUTE.map(([r, c]) => swipe.center(r, c));
  for (const [r, c] of ROUTE) swipe.fill(r, c);
  swipe.trail(pts.map(([x, y], i) => `${i ? 'L' : 'M'}${x - PAD} ${y - PAD}`).join(' '));
  swipe.put(...pts[pts.length - 1]!);
  swipe.fingerState(true, false);
}

// ---------- l'ouverture ----------

let run: Run | null = null;
let onClosed: (() => void) | null = null;

function $(id: string): HTMLElement {
  return document.getElementById(id)!;
}

function stop(): void {
  if (!run) return;
  run.alive = false;
  run.timers.forEach(clearTimeout);
  run = null;
}

/** Les minuteurs de la voix du renard, à part de ceux des animations (Run) : ils commencent dès
 *  la page d'accueil, avant qu'aucune mini-feuille n'existe. */
let voice: number[] = [];

function hush(): void {
  voice.forEach(clearTimeout);
  voice = [];
  document
    .querySelectorAll('#gestureDialog .gd-fox img.talk')
    .forEach((f) => f.classList.remove('talk'));
}

/** Le renard d'une page dit ses bulles. */
function speak(page: HTMLElement): void {
  hush();
  // La tête bouge sur l'image, l'entrée et le souffle sur son enveloppe : deux animations sur
  // le même élément se remplaceraient, et chaque fin de phrase rejouerait l'entrée.
  const fox = page.querySelector<HTMLElement>('.gd-fox img')!;
  // L'entrée du renard d'abord (cf. @keyframes gdFoxIn), puis les mots.
  const from = 260;
  const said = say([...page.querySelectorAll<HTMLElement>('.gd-say')], from);
  voice = talk(fox, from, said);
}

/** Ouvre le dialogue. `done` est appelé une fois, à la fermeture, quelle qu'en soit la voie
 *  (le bouton ou « précédent »). `welcome` ouvre sur la page où le renard se présente — à la
 *  toute première feuille seulement : « Revoir les gestes » commence à la page suivante. */
export function openGestureDialog(done: () => void, welcome = false): void {
  const dlg = $('gestureDialog');
  if (dlg.getAttribute('aria-hidden') === 'false') return;
  stop();
  onClosed = done;
  $('gdGo').onclick = () => closeGestureDialog();
  // Bonjour, se promener, colorier : se déplacer d'abord, parce que la pièce s'ouvre zoomée et
  // que regarder autour est la première chose qu'on y fait (choix de Vincent, 2026-10-31).
  $('gdNext').onclick = () => showMove();
  $('gdMoveNext').onclick = () => showGestures();
  dlg.setAttribute('aria-hidden', 'false');
  if (!welcome) return showMove();
  showPage('hello');
  $('gdNext').focus();
  speak($('gdHello'));
}

type Page = 'hello' | 'gestures' | 'move';
const PAGES: Record<Page, { id: string; title: string }> = {
  hello: { id: 'gdHello', title: 'gdHelloTitle' },
  gestures: { id: 'gdGestures', title: 'gdTitle' },
  move: { id: 'gdMove', title: 'gdMoveTitle' },
};

function showPage(page: Page): void {
  const card = $('gestureDialog').querySelector<HTMLElement>('.gd')!;
  card.dataset.page = page;
  card.setAttribute('aria-labelledby', PAGES[page].title);
  for (const [k, v] of Object.entries(PAGES)) $(v.id).hidden = k !== page;
}

/** La page « se promener » : glisser et pincer. Ses deux scènes sont en CSS seul (cf. .gd-move) ;
 *  le stop() ne sert qu'à « Revoir les gestes » rouvert par-dessus une ouverture précédente. */
function showMove(): void {
  stop();
  showPage('move');
  $('gdMoveNext').focus();
  speak($('gdMove'));
}

/** La carte des gestes. Les mini-feuilles naissent ici et non à l'ouverture : sur la page
 *  d'accueil elles tourneraient cachées, et le joueur arriverait au milieu de leur boucle. */
function showGestures(): void {
  stop();
  showPage('gestures');
  const tap = new MiniSheet($('gdTapScene'), TAP_GRID);
  const swipe = new MiniSheet($('gdSwipeScene'), SWIPE_GRID);
  $('gdGo').focus();
  speak($('gdGestures'));

  if (noMotion()) return stillFrames(tap, swipe);
  const r: Run = { alive: true, timers: [] };
  run = r;
  const swallow = (e: unknown): void => {
    if (e !== STOP) throw e;
  };
  tapLoop(r, tap).catch(swallow);
  // Décalée : les deux doigts partiraient ensemble, et l'œil ne saurait lequel suivre.
  wait(r, 1400)
    .then(() => swipeLoop(r, swipe))
    .catch(swallow);
}

/** Renvoie true quand elle a refermé quelque chose, ce qu'attend « précédent » d'Android —
 *  cf. closeOverlays() dans ui.ts. Fermer par « précédent » vaut lecture : celui qui chasse ce
 *  dialogue ne veut pas le revoir à la feuille suivante. */
export function closeGestureDialog(): boolean {
  const dlg = $('gestureDialog');
  if (dlg.getAttribute('aria-hidden') !== 'false') return false;
  dlg.setAttribute('aria-hidden', 'true');
  stop();
  hush();
  const done = onClosed;
  onClosed = null;
  done?.();
  return true;
}
